// src/modules/admin/components/ExemptionCard/index.js
import React, { useState } from 'react';
import { Badge, Button, Card, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { formatDate } from '../../../../shared/utils/dateUtils';
import { penaltyDaysLate } from '../../../../shared/utils/formatters';
import './styles.css';

const ExemptionCard = ({ exemption, onReview }) => {
    const {
        firstName,
        lastName,
        activityType,
        distance,
        date,
        rawPoints,
        penaltyFactor,
        points,
        exemptionStatus,
        exemptionUserNote,
        exemptionAdminNote,
        exemptionReviewedBy,
        exemptionReviewedAt,
    } = exemption;

    const [isReviewing, setIsReviewing] = useState(false);
    const [adminNote, setAdminNote] = useState('');
    const [showNoteInput, setShowNoteInput] = useState(false);
    const [reviewResult, setReviewResult] = useState(null);
    const [actionType, setActionType] = useState(null);

    const pointsRecovered = rawPoints - points;

    const handleReview = async (action) => {
        setReviewResult(null);
        setIsReviewing(true);
        setActionType(action);

        try {
            await onReview(exemption.activityId, action, adminNote);
            setReviewResult({ type: 'success', message: `Exemption ${action === 'approve' ? 'approved' : 'rejected'} successfully` });
        } catch (err) {
            setReviewResult({ type: 'error', message: err?.message || 'Failed to process exemption' });
        } finally {
            setIsReviewing(false);
            setActionType(null);
        }
    };

    const handleApprove = () => {
        setShowNoteInput(true);
        setReviewResult(null);
    };

    const handleReject = () => {
        setShowNoteInput(true);
        setReviewResult(null);
    };

    const statusBadgeVariant = {
        pending: 'warning',
        approved: 'success',
        rejected: 'danger',
    }[exemptionStatus] || 'secondary';

    const displayName = `${firstName} ${lastName}`;

    return (
        <Card className="exemption-card h-100">
            <Card.Header className="exemption-card-header">
                <div className="exemption-card-header-info">
                    <span className="exemption-card-username">{displayName}</span>
                    <span className="exemption-card-separator">—</span>
                    <span className="exemption-card-activity-type">{activityType}</span>
                    <span className="exemption-card-distance">{distance}km</span>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Link
                        to={`/activity/${exemption.activityId}`}
                        className="exemption-card-activity-link"
                        title="Open activity"
                    >
                        <i className="bi bi-box-arrow-up-right" />
                    </Link>
                    <Badge bg={statusBadgeVariant} className="exemption-card-status">
                        {exemptionStatus.toUpperCase()}
                    </Badge>
                </div>
            </Card.Header>

            <Card.Body className="exemption-card-body">
                <div className="exemption-card-date">
                    <strong>Activity Date:</strong> {formatDate(date, 'MMM dd, yyyy')}
                </div>

                <div className="exemption-card-section">
                    <h6 className="exemption-card-section-title">Penalty Details:</h6>
                    <ul className="exemption-card-details-list">
                        <li>
                            <span className="exemption-card-detail-label">Reason:</span>
                            <span className="exemption-card-detail-value">Logged {penaltyDaysLate(penaltyFactor)}</span>
                        </li>
                        <li>
                            <span className="exemption-card-detail-label">Raw Points:</span>
                            <span className="exemption-card-detail-value">{rawPoints}</span>
                        </li>
                        <li>
                            <span className="exemption-card-detail-label">Penalty Factor:</span>
                            <span className="exemption-card-detail-value">{penaltyFactor}x</span>
                        </li>
                        <li>
                            <span className="exemption-card-detail-label">Effective Points:</span>
                            <span className="exemption-card-detail-value">{points}</span>
                        </li>
                        <li>
                            <span className="exemption-card-detail-label">Points Recovered if Approved:</span>
                            <span className="exemption-card-detail-value exemption-card-recovered">
                                +{pointsRecovered}
                            </span>
                        </li>
                    </ul>
                </div>

                <div className="exemption-card-section">
                    <h6 className="exemption-card-section-title">User&apos;s Reason:</h6>
                    <blockquote className="exemption-card-user-note">
                        {exemptionUserNote || <em className="exemption-card-empty">No reason provided</em>}
                    </blockquote>
                </div>

                {exemptionStatus === 'pending' && (
                    <div className="exemption-card-actions">
                        <div className="exemption-card-buttons">
                            <Button
                                variant="success"
                                size="sm"
                                onClick={handleApprove}
                                disabled={isReviewing}
                            >
                                {isReviewing && actionType === 'approve' ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Approving…
                                    </>
                                ) : (
                                    'Approve'
                                )}
                            </Button>
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={handleReject}
                                disabled={isReviewing}
                            >
                                {isReviewing && actionType === 'reject' ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm me-1"
                                            role="status"
                                            aria-hidden="true"
                                        ></span>
                                        Rejecting…
                                    </>
                                ) : (
                                    'Reject'
                                )}
                            </Button>
                        </div>

                        {showNoteInput && (
                            <div className="exemption-card-note-input">
                                <Form.Group>
                                    <Form.Label className="exemption-card-note-label">
                                        Admin note (optional):
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        value={adminNote}
                                        onChange={(e) => setAdminNote(e.target.value)}
                                        placeholder="Add a note for the record…"
                                    />
                                </Form.Group>
                                <div className="exemption-card-note-actions">
                                    <Button
                                        variant="success"
                                        size="sm"
                                        onClick={() => handleReview('approve')}
                                        disabled={isReviewing}
                                    >
                                        {isReviewing && actionType === 'approve' ? 'Approving…' : 'Confirm Approve'}
                                    </Button>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleReview('reject')}
                                        disabled={isReviewing}
                                    >
                                        {isReviewing && actionType === 'reject' ? 'Rejecting…' : 'Confirm Reject'}
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                            setShowNoteInput(false);
                                            setAdminNote('');
                                            setReviewResult(null);
                                        }}
                                        disabled={isReviewing}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}

                        {reviewResult && (
                            <div className={`exemption-card-feedback exemption-card-feedback-${reviewResult.type}`}>
                                {reviewResult.message}
                            </div>
                        )}
                    </div>
                )}

                {(exemptionStatus === 'approved' || exemptionStatus === 'rejected') && (
                    <div className="exemption-card-review-info">
                        <hr className="exemption-card-divider" />
                        <div className="exemption-card-review-row">
                            <strong>Reviewed by:</strong>{' '}
                            {exemptionReviewedBy || <em>Unknown</em>}
                            {' on '}
                            {exemptionReviewedAt ? formatDate(exemptionReviewedAt, 'MMM dd, yyyy') : '—'}
                        </div>
                        {exemptionAdminNote && (
                            <div className="exemption-card-review-row">
                                <strong>Admin note:</strong> {exemptionAdminNote}
                            </div>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

ExemptionCard.propTypes = {
    /** Full exemption data object */
    exemption: PropTypes.shape({
        activityId: PropTypes.string.isRequired,
        username: PropTypes.string,
        firstName: PropTypes.string.isRequired,
        lastName: PropTypes.string.isRequired,
        activityType: PropTypes.string.isRequired,
        distance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        date: PropTypes.string.isRequired,
        rawPoints: PropTypes.number.isRequired,
        penaltyFactor: PropTypes.number.isRequired,
        points: PropTypes.number.isRequired,
        exemptionStatus: PropTypes.oneOf(['pending', 'approved', 'rejected']).isRequired,
        exemptionRequestedAt: PropTypes.string,
        exemptionUserNote: PropTypes.string,
        exemptionAdminNote: PropTypes.string,
        exemptionReviewedBy: PropTypes.string,
        exemptionReviewedAt: PropTypes.string,
    }).isRequired,
    /** Callback to handle approve/reject action */
    onReview: PropTypes.func.isRequired,
};

export default ExemptionCard;
